// src/utils/logger.ts

type LogCategory = 'TOOL' | 'CONNECTION' | 'AUDIO' | 'WEBSOCKET' | 'API' | 'UI' | 'GENERAL';

interface LogConfig {
  enabledCategories: LogCategory[];
  showTimestamp: boolean;
}

class Logger {
  private config: LogConfig = {
    enabledCategories: ['TOOL', 'CONNECTION', 'API', 'UI', 'GENERAL'], // Only show tool-related logs by default
    showTimestamp: true,
  };

  setConfig(config: Partial<LogConfig>) {
    this.config = { ...this.config, ...config };
  }

  enableAll() {
    this.config.enabledCategories = ['TOOL', 'CONNECTION', 'AUDIO', 'WEBSOCKET', 'API', 'UI', 'GENERAL'];
  }

  enableOnlyTools() {
    this.config.enabledCategories = ['TOOL'];
  }

  private shouldLog(category: LogCategory): boolean {
    return this.config.enabledCategories.includes(category);
  }

  private formatMessage(emoji: string, tag: string, ...args: any[]): any[] {
    const timestamp = this.config.showTimestamp ? `[${new Date().toLocaleTimeString()}]` : '';
    return [timestamp, emoji, tag, ...args].filter(Boolean);
  }

  // Tool-related logs (ENABLED by default)
  tool(tag: string, ...args: any[]) {
    if (this.shouldLog('TOOL')) {
      console.log(...this.formatMessage('🔧', `[TOOL] ${tag}`, ...args));
    }
  }

  toolCall(functionName: string, args?: any) {
    if (this.shouldLog('TOOL')) {
      console.log(...this.formatMessage('📞', `[TOOL CALL]`, functionName, args));
    }
  }

  toolResponse(functionName: string, response: any) {
    if (this.shouldLog('TOOL')) {
      console.log(...this.formatMessage('📥', `[TOOL RESPONSE]`, functionName, response));
    }
  }

  // Connection logs (DISABLED by default)
  connection(...args: any[]) {
    if (this.shouldLog('CONNECTION')) {
      console.log(...this.formatMessage('🔌', '[CONNECTION]', ...args));
    }
  }

  // Audio logs (DISABLED by default)
  audio(...args: any[]) {
    if (this.shouldLog('AUDIO')) {
      console.log(...this.formatMessage('🔊', '[AUDIO]', ...args));
    }
  }

  // WebSocket logs (DISABLED by default)
  websocket(...args: any[]) {
    if (this.shouldLog('WEBSOCKET')) {
      console.log(...this.formatMessage('🌐', '[WEBSOCKET]', ...args));
    }
  }

  // API logs (DISABLED by default)
  api(...args: any[]) {
    if (this.shouldLog('API')) {
      console.log(...this.formatMessage('📡', '[API]', ...args));
    }
  }

  // UI logs (DISABLED by default)
  ui(...args: any[]) {
    if (this.shouldLog('UI')) {
      console.log(...this.formatMessage('🖥️', '[UI]', ...args));
    }
  }

  // General logs (DISABLED by default)
  info(...args: any[]) {
    if (this.shouldLog('GENERAL')) {
      console.log(...this.formatMessage('ℹ️', '[INFO]', ...args));
    }
  }

  // Errors (ALWAYS shown)
  error(...args: any[]) {
    console.error(...this.formatMessage('❌', '[ERROR]', ...args));
  }

  // Warnings (ALWAYS shown)
  warn(...args: any[]) {
    console.warn(...this.formatMessage('⚠️', '[WARN]', ...args));
  }
}

export const logger = new Logger();

// Expose globally for easy access in console
if (typeof window !== 'undefined') {
  (window as any).logger = logger;
}
